import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  MessageCircle, 
  ThumbsUp, 
  Heart, 
  AlertTriangle,
  Ban,
  Eye,
  EyeOff,
  Send,
  TrendingUp,
  BarChart3,
  Settings,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  Shield,
  Sparkles,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock brand ID - in real app, this would come from context/state
const MOCK_BRAND_ID = 'test_brand_all';

export default function Community() {
  const [activeTab, setActiveTab] = useState('comments');
  const [comments, setComments] = useState([]);
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [settings, setSettings] = useState(null);
  const [replySuggestions, setReplySuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [platformFilter, setPlatformFilter] = useState('all');
  const [classificationFilter, setClassificationFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadComments();
  }, [platformFilter, classificationFilter, priorityFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadComments(),
        loadStats(),
        loadInsights(),
        loadSettings(),
        loadReplySuggestions()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load community data');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const params = new URLSearchParams();
      params.append('brand_id', MOCK_BRAND_ID);
      if (platformFilter !== 'all') params.append('platform', platformFilter);
      if (classificationFilter !== 'all') params.append('classification', classificationFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      params.append('limit', '50');

      const res = await fetch(`${API}/community/comments?${params}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setComments(data.comments || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API}/community/stats/${MOCK_BRAND_ID}?days=7`, {
        credentials: 'include'
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadInsights = async () => {
    try {
      const res = await fetch(`${API}/community/insights/${MOCK_BRAND_ID}?days=7`, {
        credentials: 'include'
      });
      const data = await res.json();
      setInsights(data.insights || []);
    } catch (error) {
      console.error('Error loading insights:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API}/community/settings/${MOCK_BRAND_ID}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadReplySuggestions = async () => {
    try {
      const res = await fetch(`${API}/community/replies/suggestions/${MOCK_BRAND_ID}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setReplySuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Error loading reply suggestions:', error);
    }
  };

  const triggerIngestion = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/community/ingestion/trigger/${MOCK_BRAND_ID}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      
      toast.success(`Ingested comments from all platforms`);
      await loadData();
    } catch (error) {
      console.error('Error triggering ingestion:', error);
      toast.error('Failed to ingest comments');
    } finally {
      setRefreshing(false);
    }
  };

  const updateSettings = async (key, value) => {
    try {
      const params = new URLSearchParams({ [key]: value });
      const res = await fetch(`${API}/community/settings/${MOCK_BRAND_ID}?${params}`, {
        method: 'PUT',
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.message) {
        toast.success('Settings updated');
        await loadSettings();
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const getClassificationColor = (classification) => {
    const colors = {
      praise: 'bg-green-100 text-green-800',
      question: 'bg-blue-100 text-blue-800',
      suggestion: 'bg-purple-100 text-purple-800',
      complaint: 'bg-orange-100 text-orange-800',
      spam: 'bg-red-100 text-red-800',
      harassment: 'bg-red-200 text-red-900',
      off_topic: 'bg-gray-100 text-gray-800',
      other: 'bg-gray-100 text-gray-600'
    };
    return colors[classification] || colors.other;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-500',
      high: 'bg-orange-500',
      normal: 'bg-blue-500',
      low: 'bg-gray-400'
    };
    return colors[priority] || colors.normal;
  };

  const getSentimentIcon = (score) => {
    if (score > 0.3) return { icon: <ThumbsUp className="h-4 w-4" />, color: 'text-green-600' };
    if (score < -0.3) return { icon: <AlertTriangle className="h-4 w-4" />, color: 'text-red-600' };
    return { icon: <MessageCircle className="h-4 w-4" />, color: 'text-gray-600' };
  };

  const filteredComments = comments.filter(comment => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return comment.text.toLowerCase().includes(query) || 
             comment.author.toLowerCase().includes(query);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading community data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
              <MessageCircle className="h-8 w-8 text-pink-500" />
              Community Manager
            </h1>
            <p className="text-[#8A93A6]">
              AI-powered comment management and engagement
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={triggerIngestion}
              disabled={refreshing}
              variant="outline"
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Comments
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8A93A6] mb-1">Total Comments</p>
                  <p className="text-2xl font-bold text-white">{stats.total_comments || 0}</p>
                </div>
                <MessageCircle className="h-8 w-8 text-pink-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8A93A6] mb-1">Auto Replies</p>
                  <p className="text-2xl font-bold text-white">{stats.auto_replied || 0}</p>
                </div>
                <Send className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8A93A6] mb-1">Moderated</p>
                  <p className="text-2xl font-bold text-white">{stats.auto_moderated || 0}</p>
                </div>
                <Shield className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8A93A6] mb-1">Reply Rate</p>
                  <p className="text-2xl font-bold text-white">
                    {stats.total_comments > 0 
                      ? Math.round((stats.auto_replied / stats.total_comments) * 100) 
                      : 0}%
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl glass">
          <TabsTrigger value="comments" className="gap-2">
            <MessageCircle className="h-4 w-4" />
            Comments
          </TabsTrigger>
          <TabsTrigger value="replies" className="gap-2">
            <Send className="h-4 w-4" />
            Replies
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Insights
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Comments Tab */}
        <TabsContent value="comments" className="space-y-4">
          {/* Filters */}
          <Card className="glass">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm text-[#8A93A6] mb-2 block">Search</label>
                  <Input
                    placeholder="Search comments..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-[#1A1D25] border-[#2A2D35]"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-[#8A93A6] mb-2 block">Platform</label>
                  <Select value={platformFilter} onValueChange={setPlatformFilter}>
                    <SelectTrigger className="bg-[#1A1D25] border-[#2A2D35]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Platforms</SelectItem>
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm text-[#8A93A6] mb-2 block">Classification</label>
                  <Select value={classificationFilter} onValueChange={setClassificationFilter}>
                    <SelectTrigger className="bg-[#1A1D25] border-[#2A2D35]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="praise">Praise</SelectItem>
                      <SelectItem value="question">Questions</SelectItem>
                      <SelectItem value="suggestion">Suggestions</SelectItem>
                      <SelectItem value="complaint">Complaints</SelectItem>
                      <SelectItem value="spam">Spam</SelectItem>
                      <SelectItem value="harassment">Harassment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm text-[#8A93A6] mb-2 block">Priority</label>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger className="bg-[#1A1D25] border-[#2A2D35]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comments List */}
          <div className="space-y-3">
            {filteredComments.length === 0 ? (
              <Card className="glass">
                <CardContent className="pt-6 text-center py-12">
                  <MessageCircle className="h-12 w-12 text-[#8A93A6] mx-auto mb-4" />
                  <p className="text-[#8A93A6]">No comments found. Try adjusting your filters.</p>
                </CardContent>
              </Card>
            ) : (
              filteredComments.map((comment) => {
                const sentiment = getSentimentIcon(comment.sentiment_score);
                return (
                  <Card key={comment.id} className="glass hover:border-[#3A7BFF]/50 transition-colors">
                    <CardContent className="pt-6">
                      <div className="flex gap-4">
                        {/* Priority Indicator */}
                        <div className="flex flex-col items-center gap-2">
                          <div 
                            className={`w-1 h-full rounded-full ${getPriorityColor(comment.priority_level)}`}
                          />
                        </div>

                        {/* Comment Content */}
                        <div className="flex-1">
                          {/* Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-white">{comment.author}</span>
                                <Badge variant="outline" className="text-xs">
                                  {comment.platform}
                                </Badge>
                                <Badge className={getClassificationColor(comment.classification)}>
                                  {comment.classification}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-[#8A93A6]">
                                <Clock className="h-3 w-3" />
                                {new Date(comment.timestamp).toLocaleString()}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className={`${sentiment.color}`}>
                                {sentiment.icon}
                              </div>
                              {comment.toxicity_score > 0.7 && (
                                <AlertTriangle className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                          </div>

                          {/* Comment Text */}
                          <p className="text-white mb-3">{comment.text}</p>

                          {/* Actions & Status */}
                          <div className="flex items-center justify-between">
                            <div className="flex gap-2">
                              {comment.auto_replied && (
                                <Badge variant="outline" className="text-xs gap-1">
                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                  Auto-Replied
                                </Badge>
                              )}
                              {comment.auto_moderated && (
                                <Badge variant="outline" className="text-xs gap-1">
                                  <Shield className="h-3 w-3 text-orange-500" />
                                  Auto-Moderated
                                </Badge>
                              )}
                              {comment.hidden && (
                                <Badge variant="outline" className="text-xs gap-1">
                                  <EyeOff className="h-3 w-3" />
                                  Hidden
                                </Badge>
                              )}
                              {comment.blocked_author && (
                                <Badge variant="outline" className="text-xs gap-1 text-red-500">
                                  <Ban className="h-3 w-3" />
                                  Author Blocked
                                </Badge>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost">
                                <Send className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost">
                                {comment.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                              </Button>
                              <Button size="sm" variant="ghost">
                                <Ban className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Reply if available */}
                          {comment.reply_text && (
                            <div className="mt-3 pl-4 border-l-2 border-blue-500 bg-blue-500/5 p-3 rounded-r">
                              <p className="text-sm text-[#8A93A6] mb-1 font-medium">Your Reply:</p>
                              <p className="text-sm text-white">{comment.reply_text}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Reply Suggestions Tab */}
        <TabsContent value="replies" className="space-y-4">
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-yellow-500" />
                Pending Reply Suggestions
              </CardTitle>
              <CardDescription>
                AI-generated reply suggestions waiting for your approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              {replySuggestions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-[#8A93A6]">No pending reply suggestions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {replySuggestions.map((suggestion) => (
                    <div key={suggestion.id} className="p-4 border border-[#2A2D35] rounded-lg">
                      <div className="mb-3">
                        <p className="text-sm text-[#8A93A6] mb-2">Original Comment:</p>
                        <p className="text-white">{suggestion.comment_text}</p>
                      </div>
                      <div className="mb-3">
                        <p className="text-sm text-[#8A93A6] mb-2">Suggested Reply:</p>
                        <Textarea 
                          defaultValue={suggestion.suggested_reply}
                          className="bg-[#1A1D25] border-[#2A2D35]"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="gap-2">
                          <CheckCircle className="h-4 w-4" />
                          Approve & Send
                        </Button>
                        <Button size="sm" variant="outline">
                          <XCircle className="h-4 w-4" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-4">
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                Community Insights (Last 7 Days)
              </CardTitle>
              <CardDescription>
                AI-generated insights about your community trends
              </CardDescription>
            </CardHeader>
            <CardContent>
              {insights.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-[#8A93A6]">No insights available yet. Check back after ingesting more comments.</p>
                  <Button 
                    className="mt-4"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/community/insights/${MOCK_BRAND_ID}/generate?days=7`, {
                          method: 'POST',
                          credentials: 'include'
                        });
                        const data = await res.json();
                        toast.success('Insights generated!');
                        await loadInsights();
                      } catch (error) {
                        toast.error('Failed to generate insights');
                      }
                    }}
                  >
                    Generate Insights
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {insights.map((insight) => (
                    <div key={insight.id} className="p-4 border border-[#2A2D35] rounded-lg">
                      <div className="flex items-start gap-3">
                        <BarChart3 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <Badge className="mb-2">{insight.trend_type}</Badge>
                          <p className="text-white">{insight.text}</p>
                          <p className="text-xs text-[#8A93A6] mt-2">
                            {new Date(insight.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Community Manager Settings</CardTitle>
              <CardDescription>
                Configure how the AI Community Manager handles comments
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {settings && (
                <>
                  {/* Enable Manager */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">Enable Community Manager</h3>
                      <p className="text-sm text-[#8A93A6]">Turn on AI-powered comment management</p>
                    </div>
                    <Switch
                      checked={settings.enable_manager}
                      onCheckedChange={(checked) => updateSettings('enable_manager', checked)}
                    />
                  </div>

                  <div className="border-t border-[#2A2D35] pt-6" />

                  {/* Auto Reply */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">Auto Reply</h3>
                      <p className="text-sm text-[#8A93A6]">Automatically reply to comments (requires approval)</p>
                    </div>
                    <Switch
                      checked={settings.enable_auto_reply}
                      onCheckedChange={(checked) => updateSettings('enable_auto_reply', checked)}
                      disabled={!settings.enable_manager}
                    />
                  </div>

                  <div className="border-t border-[#2A2D35] pt-6" />

                  {/* Auto Hide Spam */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">Auto-Hide Spam</h3>
                      <p className="text-sm text-[#8A93A6]">Automatically hide spam comments</p>
                    </div>
                    <Switch
                      checked={settings.enable_auto_hide_spam}
                      onCheckedChange={(checked) => updateSettings('enable_auto_hide_spam', checked)}
                      disabled={!settings.enable_manager}
                    />
                  </div>

                  <div className="border-t border-[#2A2D35] pt-6" />

                  {/* Auto Block Harassment */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">Auto-Block Harassment</h3>
                      <p className="text-sm text-[#8A93A6]">Automatically block users posting harassment</p>
                    </div>
                    <Switch
                      checked={settings.enable_auto_block_harassment}
                      onCheckedChange={(checked) => updateSettings('enable_auto_block_harassment', checked)}
                      disabled={!settings.enable_manager}
                    />
                  </div>

                  <div className="border-t border-[#2A2D35] pt-6" />

                  {/* Brand Voice */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Brand Voice</h3>
                    <p className="text-sm text-[#8A93A6] mb-3">Choose the tone for AI-generated replies</p>
                    <Select 
                      value={settings.brand_voice}
                      onValueChange={(value) => updateSettings('brand_voice', value)}
                      disabled={!settings.enable_manager}
                    >
                      <SelectTrigger className="bg-[#1A1D25] border-[#2A2D35] max-w-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Link to Autopilot */}
          <Card className="glass border-orange-500/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-8 w-8 text-orange-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-white">Community Autopilot</h3>
                    <p className="text-sm text-[#8A93A6]">Fully automate comment replies and moderation</p>
                  </div>
                </div>
                <Button 
                  onClick={() => window.location.href = '/settings/autopilot'}
                  variant="outline"
                >
                  Configure →
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
